import { useRef, useCallback, useEffect } from "react";
import { onPageLeave } from "#app/lib/page-leave";

export function useEditorAutosave(
  save: (html: string) => Promise<void>,
  delay: number = 1200,
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestHtmlRef = useRef<string>("");
  const savedHtmlRef = useRef<string>("");

  const setInitialHtml = useCallback((html: string) => {
    savedHtmlRef.current = html;
    latestHtmlRef.current = html;
  }, []);

  const onUpdate = useCallback(
    (html: string) => {
      latestHtmlRef.current = html;

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        if (latestHtmlRef.current !== savedHtmlRef.current) {
          save(latestHtmlRef.current);
          savedHtmlRef.current = latestHtmlRef.current;
        }
      }, delay);
    },
    [save, delay],
  );

  /** Commit whatever the debounce is still holding, now. The host calls it
   *  when the editor leaves the screen before the component that owns this
   *  hook does — a click-away that swaps the editor for its read state. */
  const flush = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    if (latestHtmlRef.current !== savedHtmlRef.current) {
      save(latestHtmlRef.current);
      savedHtmlRef.current = latestHtmlRef.current;
    }
  }, [save]);

  // A reload or a closed tab unloads the page without unmounting anything,
  // so the debounce is flushed when the page hides too. `first`: a host
  // store with a debounce of its own (a widget's config) flushes after it.
  useEffect(() => onPageLeave(flush, "first"), [flush]);

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        if (latestHtmlRef.current !== savedHtmlRef.current) {
          save(latestHtmlRef.current);
        }
      }
    };
  }, [save]);

  return { onUpdate, setInitialHtml, flush };
}
